import React, { useState } from 'react';
import theme from '@/constants/theme';
import Button from '@/components/input/Button';
import { Add } from '@mui/icons-material';
import {
  MultipleChoiceQuestion,
  Question,
  Quiz,
  RangeQuestion,
  SingleChoiceQuestion,
  typeDetails,
} from '../../common';
import { nanoid } from 'nanoid';

interface Props {
  quiz: Quiz | null;
  setQuiz: React.Dispatch<React.SetStateAction<Quiz | null>>;
}

const AddQuestionButton: React.FC<Props> = ({ quiz, setQuiz }) => {
  const [open, setOpen] = useState(false);

  const handleAddQuestion = (selectedType: Question['type']) => {
    const prevQuestions = quiz?.questions ?? [];
    const generatedId = nanoid(6);
    switch (selectedType) {
      case 'text': {
        setQuiz((prevState) => ({
          ...prevState,
          questions: [
            ...prevQuestions,
            {
              id: generatedId,
              type: 'text',
              question: '',
            },
          ],
        }));
        break;
      }
      case 'multipleChoice': {
        setQuiz((prevState) => ({
          ...prevState,
          questions: [
            ...prevQuestions,
            {
              id: generatedId,
              type: 'multipleChoice',
              question: '',
              options: ['', '', ''],
            } as MultipleChoiceQuestion,
          ],
        }));
        break;
      }
      case 'singleChoice': {
        setQuiz((prevState) => ({
          ...prevState,
          questions: [
            ...prevQuestions,
            {
              id: generatedId,
              type: 'singleChoice',
              question: '',
              options: ['', '', ''],
            } as SingleChoiceQuestion,
          ],
        }));
        break;
      }
      case 'open': {
        setQuiz((prevState) => ({
          ...prevState,
          questions: [
            ...prevQuestions,
            {
              id: generatedId,
              type: 'open',
              question: '',
            },
          ],
        }));
        break;
      }
      case 'range': {
        setQuiz((prevState) => ({
          ...prevState,
          questions: [
            ...prevQuestions,
            {
              id: generatedId,
              type: 'range',
              question: '',
              options: {
                min: "0",
                max: "10",
                step: "1",
              },
            } as RangeQuestion,
          ],
        }));
        break;
      }
      default:
        break;
    }
    setOpen(false);
  };

  const questionTypes = Object.keys(typeDetails).map((type) => ({
    type,
    Icon: typeDetails[type as Question['type']].Icon,
    handleAddQuestion: () => handleAddQuestion(type as Question['type']),
  }));

  return (
    <div style={{ position: 'relative' }}>
      <Button type="button" variant="outline" Icon={Add} onClick={() => setOpen(!open)}>
        Añadir pregunta
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 50,
            left: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            backgroundColor: theme.colors.background.secondary,
            borderRadius: 8,
            padding: 8,
            color: theme.colors.text.body,
            width: 200,
            zIndex: 10,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          }}
        >
          {questionTypes.map(({ type, Icon, handleAddQuestion }) => (
            <Button key={type} Icon={Icon} size="small" variant="ghost" type="button" onClick={handleAddQuestion}>
              {typeDetails[type as Question['type']].label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddQuestionButton;
